import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type WorkflowWithActionGroups,
  type ActionGroup,
  type WeeklySchedule,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'
import { sanitizeActionGroupsForApi } from '../utils/handoff-workflow-utils'

export interface HandoffWorkflowRequest {
  title: string
  selectionCriteria: string
  propertyIds?: string[]
  groupTypes?: string[]
  officeHours: string
  customOfficeHoursSchedule?: WeeklySchedule
  handoffInstructions: string
  actionGroups: ActionGroup[]
}

export interface UpdateHandoffWorkflowRequest
  extends Partial<HandoffWorkflowRequest> {}

export class WorkflowsApiClient extends BaseApiClient {
  /**
   * Get all workflows for the current company
   * @returns WorkflowWithActionGroups[]
   */
  async getWorkflows(): Promise<WorkflowWithActionGroups[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups[]>
        >(API_ROUTES.WORKFLOWS.GET_ALL)
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching workflows',
      },
    )
    return result || []
  }

  /**
   * Get handoff workflows for the current company
   * @returns WorkflowWithActionGroups[]
   */
  async getHandoffWorkflows(): Promise<WorkflowWithActionGroups[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups[]>
        >(API_ROUTES.WORKFLOWS.GET_HANDOFF)
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching handoff workflows',
      },
    )
    return result || []
  }

  /**
   * [ADMIN] Get all handoff workflows for a specific company
   * @param companyId - The company ID to fetch handoff workflows for
   * @returns WorkflowWithActionGroups[]
   */
  async getAllAdminHandoffWorkflowsForCompany(
    companyId: string,
  ): Promise<WorkflowWithActionGroups[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups[]>
        >(
          `${API_ROUTES.ADMIN.WORKFLOWS.GET_HANDOFF_BY_COMPANY}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error fetching handoff workflows for company ${companyId}`,
      },
    )
    return result || []
  }

  /**
   * Get a specific workflow by ID
   * @param id - The ID of the workflow to get
   * @returns WorkflowWithActionGroups | null
   */
  async getWorkflowById(id: string): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.GET, { id }))
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error fetching workflow ${id}`,
      },
    )
  }

  /**
   * Create a new handoff workflow
   * @param data - The workflow data to create
   * @returns WorkflowWithActionGroups | null
   */
  async createHandoffWorkflow(
    data: HandoffWorkflowRequest,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const sanitizedData = {
          ...data,
          actionGroups: sanitizeActionGroupsForApi(data.actionGroups),
        }
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(API_ROUTES.WORKFLOWS.CREATE_HANDOFF, {
          method: 'POST',
          body: JSON.stringify(sanitizedData),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating handoff workflow',
      },
    )
  }

  /**
   * [ADMIN] Create a new handoff workflow for a specific company
   * @param companyId - The company ID to create the workflow for
   * @param data - The workflow data to create
   * @returns WorkflowWithActionGroups | null
   */
  async createAdminHandoffWorkflowForCompany(
    companyId: string,
    data: HandoffWorkflowRequest,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const sanitizedData = {
          ...data,
          actionGroups: sanitizeActionGroupsForApi(data.actionGroups),
        }
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(
          `${API_ROUTES.ADMIN.WORKFLOWS.CREATE_HANDOFF}?companyId=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            body: JSON.stringify(sanitizedData),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error creating admin handoff workflow for company ${companyId}`,
      },
    )
  }

  /**
   * Duplicate an existing handoff workflow
   * @param id - The ID of the workflow to duplicate
   * @returns WorkflowWithActionGroups | null
   */
  async duplicateHandoffWorkflow(
    id: string,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.DUPLICATE_HANDOFF, { id }), {
          method: 'POST',
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error duplicating handoff workflow ${id}`,
      },
    )
  }

  /**
   * [ADMIN] Duplicate an existing handoff workflow for a specific company
   * @param id - The ID of the workflow to duplicate
   * @param companyId - The company ID that owns the workflow
   * @returns WorkflowWithActionGroups | null
   */
  async duplicateAdminHandoffWorkflowForCompany(
    id: string,
    companyId: string,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.WORKFLOWS.DUPLICATE_HANDOFF, { id })}?companyId=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error duplicating admin handoff workflow ${id} for company ${companyId}`,
      },
    )
  }

  /**
   * Update an existing handoff workflow
   * @param id - The ID of the workflow to update
   * @param data - The handoff workflow data to update
   * @returns WorkflowWithActionGroups | null
   */
  async updateHandoffWorkflow(
    id: string,
    data: UpdateHandoffWorkflowRequest,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const sanitizedData = data.actionGroups
          ? {
              ...data,
              actionGroups: sanitizeActionGroupsForApi(data.actionGroups),
            }
          : data
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.UPDATE_HANDOFF, { id }), {
          method: 'PUT',
          body: JSON.stringify(sanitizedData),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error updating handoff workflow ${id}`,
      },
    )
  }

  /**
   * [ADMIN] Update an existing handoff workflow for a specific company
   * @param id - The ID of the workflow to update
   * @param companyId - The company ID that owns the workflow
   * @param data - The handoff workflow data to update
   * @returns WorkflowWithActionGroups | null
   */
  async updateAdminHandoffWorkflowForCompany(
    id: string,
    companyId: string,
    data: UpdateHandoffWorkflowRequest,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const sanitizedData = data.actionGroups
          ? {
              ...data,
              actionGroups: sanitizeActionGroupsForApi(data.actionGroups),
            }
          : data
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.WORKFLOWS.UPDATE_HANDOFF, { id })}?companyId=${encodeURIComponent(companyId)}`,
          {
            method: 'PUT',
            body: JSON.stringify(sanitizedData),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error updating admin handoff workflow ${id} for company ${companyId}`,
      },
    )
  }

  /**
   * Update an existing workflow
   * @param id - The ID of the workflow to update
   * @param data - The workflow data to update
   * @returns WorkflowWithActionGroups | null
   */
  async updateWorkflow(
    id: string,
    data: Partial<WorkflowWithActionGroups>,
  ): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error updating workflow ${id}`,
      },
    )
  }

  /**
   * Delete a workflow
   * @param id - The ID of the workflow to delete
   * @returns boolean - True if successful
   */
  async deleteWorkflow(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request<ApiResponse<{ success: boolean }>>(
          interpolateRoute(API_ROUTES.WORKFLOWS.DELETE, { id }),
          {
            method: 'DELETE',
          },
        )
        return true
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error deleting workflow ${id}`,
      },
    )
    return result || false
  }

  /**
   * Enable a workflow
   * @param id - The ID of the workflow to enable
   * @returns WorkflowWithActionGroups | null
   */
  async enableWorkflow(id: string): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.ENABLE, { id }), {
          method: 'POST',
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error enabling workflow ${id}`,
      },
    )
  }

  /**
   * Disable a workflow
   * @param id - The ID of the workflow to disable
   * @returns WorkflowWithActionGroups | null
   */
  async disableWorkflow(id: string): Promise<WorkflowWithActionGroups | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<WorkflowWithActionGroups>
        >(interpolateRoute(API_ROUTES.WORKFLOWS.DISABLE, { id }), {
          method: 'POST',
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error disabling workflow ${id}`,
      },
    )
  }
}

// Export a singleton instance
export const workflowsApiClient = new WorkflowsApiClient()
